@use '../../style/globals.scss' as *;

$font-size-sm: 14px;
$font-size-md: 17px;
$font-size-lg: 21px;
$font-size-xl: 27px;

$foreground-primary: white;
$foreground-secondary: #797979;
$foreground-tertiary: #404040;
$foreground-alt: #2c2c2c;

$background-primary: #111111;
$background-secondary: #1b1b1b;
$background-tertiary: #181818;
$background-highlight: #313131;
$background-alt: #d9d9d9;
$shadow: rgba(0, 0, 0, 0.8);

.ProfileDrawer {
    position: absolute;
    z-index: 120;
    top: 0;
    right: -($sidebar-width);
    overflow: hidden;
    width: 100%;
    height: 100%;
    background-color: $background-tertiary;
    color: $foreground-primary;
    transition: $sidebar-transition, opacity 0s linear 0.5s;
    opacity: 0;
    font-family: Inter, sans-serif;

    @media (max-width: $bp-mobile-chat) {
        right: -100%;
        width: 100%;
    }

    &.isOpen {
        right: 0;
        transition: $sidebar-transition, opacity 0s linear 0s;
        opacity: 1;
    }

    .closeButton {
        position: absolute;
        top: 10px;
        right: 20px;
        z-index: 10;
    }

    .scrollContainer {
        @include scrollbar-styles;
        position: relative;
        overflow-y: scroll;
        overflow-x: hidden;
        height: 100%;
    }

    .pageBgImage {
        position: absolute;
        top: 0px;
        left: 0px;
        width: 100%;
        height: 400px;
        background-position: center;
        background-size: cover;
        // filter: blur(3px);
        background-repeat: no-repeat;
        clip-path: polygon(0 0, 100% 0, 100% 100%, 0 220px);
        opacity: 0.7;
    }

    .content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 112px 10px 10px 10px;
        padding-top: 112px;
        gap: 20px;

        & * {
            z-index: 2;
        }

        button:not(:disabled) {
            &:hover,
            &:focus {
                filter: brightness(1.4);
            }
        }

        .ctaButtonsContainer {
            position: relative;
            display: flex;
            gap: 10px;

            button {
                display: flex;
                justify-content: space-evenly;
                padding: 5px 20px;
                background: $background-alt;
                color: $foreground-alt;
                font-size: $font-size-sm;
                font-weight: 500;
                border-radius: 10px;

                svg {
                    width: 16px;
                    margin-left: 8px;
                    path {
                        fill: $foreground-alt;
                    }
                }
            }

            .shareError {
                position: absolute;
                bottom: -20px;
                left: 50%;
                width: 300px;
                color: $foreground-secondary;
                font-size: 10px;
                text-align: center;
                transform: translateX(-50%);
            }
        }

        .subtitle {
            color: $foreground-secondary;
            font-size: $font-size-sm;
        }

        .largerText {
            font-size: $font-size-lg;
            font-weight: 500;
        }

        .statTitle {
            color: $foreground-primary;
            font-size: $font-size-md;
            font-weight: bold;
        }

        .icon {
            width: 32px;
            height: 32px;

            fill: $foreground-secondary;

            &.active {
                fill: $foreground-primary;
            }
        }

        .scorecard {
            position: relative;
            width: 404px;
            height: 347px; // Important for image export sharing
            max-width: 100%;
            padding: 20px;
            background-color: $background-primary;
            border-radius: 10px;
            overflow: hidden;
            filter: drop-shadow(5px 5px 8px $shadow);

            .scorecardContent {
                position: relative;
            }

            .divider {
                position: relative;
                z-index: 0 !important;
                width: 100%;
                height: 2px;
                opacity: 0.2;
                border-top: 1px solid $foreground-primary;
            }

            .bgImage {
                position: absolute;
                z-index: 1;
                top: 0px;
                left: 0px;
                width: 100%;
                height: 100%;
                background-position: right;
                background-size: auto 100%;
                background-repeat: no-repeat;
                // filter: blur(3px);
                clip-path: polygon(300px 0%, 100% 0%, 100% 100%, 150px 100%);
                transform: scale(1.2);
            }

            .topBar {
                display: flex;
                justify-content: space-between;
                width: 100%;
                height: 66px;
                padding-top: 14px;

                .brandLogo {
                    height: 35px;
                    max-width: 270px;
                    object-fit: contain;
                }

                .platformLogo {
                    width: 45px;
                    height: 45px;
                    fill: $foreground-primary;
                    margin-top: -10px;
                }
            }

            .titleBar {
                display: flex;
                align-items: end;
                height: 64px;
                padding-bottom: 10px;

                h2 {
                    width: 200px;
                    font-size: $font-size-xl;
                    font-weight: 500;
                    line-height: 32px;
                    word-break: keep-all;

                    &.isLong {
                        font-size: 21px;

                        &.isExtraLong {
                            font-size: 16px;
                        }
                    }
                }

                .username {
                    position: relative;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    overflow: hidden;
                    min-height: 20px;
                    padding: 2px 10px;
                    background-color: $background-secondary;
                    font-size: $font-size-sm;
                    border-radius: 10px;
                    gap: 6px;
                    box-shadow: 0px 4px 4px 0px $shadow;

                    svg {
                        width: 16px;
                        height: 16px;
                    }
                }
            }

            .levelBadge {
                position: absolute;
                right: 0px;
                bottom: 0px;
                height: 123px;
                filter: drop-shadow(5px 5px 8px $shadow);

                @media (max-width: $bp-xs) {
                    height: 95px;
                }
            }

            .statBoxContainer {
                display: grid;
                grid-template-columns: repeat(1fr 1fr);
                grid-template-rows: auto auto;
                width: 274px;
                max-width: 100%;
                padding-bottom: 20px;
                margin-top: 20px;
                gap: 10px;

                .statBox {
                    position: relative;
                    display: flex;
                    align-items: center;
                    overflow: hidden;
                    width: 132px;
                    height: 55px;
                    padding: 10px;
                    background-color: $background-secondary;
                    border-radius: 10px;
                    gap: 5px;

                    .statTextContainer {
                        display: flex;
                        flex-direction: column;
                        line-height: 20px;
                        flex-shrink: 0;

                        .statTitle.isLong {
                            font-size: $font-size-sm;
                        }

                        .subtitle.isLong {
                            font-size: 11px;
                        }
                    }

                    &.statBoxLong {
                        grid-column: span 2;
                        width: 100%;
                        gap: 20px;

                        .statTitle {
                            color: $foreground-primary;
                            font-size: $font-size-lg;
                        }
                    }
                }
            }
        }

        .yourStats {
            width: 404px;
            max-width: 100%;

            h2 {
                padding-bottom: 10px;
                font-size: $font-size-md;
                font-weight: 500;

                svg {
                    margin-right: 10px;
                }
            }

            .gridContainer {
                display: grid;
                grid-template-columns: 1fr 1fr;
                grid-template-rows: 91px 91px;
                width: 100%;
                gap: 10px;
                line-height: 20px;

                .box {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    background-color: $background-primary;
                    border-radius: 10px;
                }

                .level {
                    position: relative;
                    grid-row: span 2;
                    flex-direction: column;
                    overflow: hidden;
                    padding: 16px;

                    .levelBadge {
                        width: 118px;
                        height: 118px;
                    }
                }

                .progress {
                    width: 60px;
                }

                .statBox {
                    position: relative;
                    display: flex;
                    justify-content: flex-start;
                    align-items: center;
                    overflow: hidden;
                    padding-right: 5px;

                    div {
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .left {
                        display: flex;
                        flex-shrink: 0;
                        justify-content: flex-start;
                        align-items: center;
                        width: 85px;
                    }
                }
                .quest {
                    .icon {
                        width: 45px;
                        height: 45px;
                    }
                }

                .titlesNumber {
                    color: $foreground-secondary;
                    font-size: $font-size-lg;
                    font-weight: 500;

                    .highlight {
                        color: $foreground-primary;
                    }
                }
            }
        }

        .tabGroup {
            width: 404px;
            max-width: 100%;

            .tabButtons {
                margin-bottom: 20px;

                button {
                    display: inline-flex;
                    justify-content: flex-start;
                    align-items: center;
                    min-width: 108px;
                    height: 38px;
                    padding: 10px;
                    margin-right: 10px;
                    color: $foreground-secondary;
                    font-size: $font-size-md;
                    font-weight: 500;
                    border-radius: 10px;
                    gap: 10px;

                    svg {
                        path {
                            fill: $foreground-secondary;
                        }
                    }

                    &.active {
                        background-color: $background-highlight;
                        color: $foreground-primary;

                        svg {
                            path {
                                fill: $foreground-primary;
                            }
                        }
                    }

                    svg {
                        width: 25px;
                    }
                }
            }

            .tabGroupLoader {
                position: absolute;
                height: 300px;
                width: 100%;
                border-radius: 10px;
            }

            .levelInfoBoxes {
                position: relative;
                display: flex;
                flex-direction: column;
                justify-content: center;
                gap: 10px;

                .levelInfoBox {
                    position: relative;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    min-height: 77px;
                    padding-right: 20px;
                    padding-left: 85px;
                    background-color: $background-primary;
                    border-radius: 10px;

                    &.hasLine {
                        &::after {
                            content: '';
                            position: absolute;
                            top: 10px;
                            left: 33px;
                            width: 2px;
                            height: 100%;
                            background-color: $foreground-primary;
                        }
                    }

                    &:last-child::after {
                        display: none;
                    }

                    .levelBadge {
                        position: absolute;
                        top: -5px;
                        left: 0px;
                        height: 67px;
                        filter: drop-shadow(5px 5px 8px $shadow);
                    }

                    .textContainer {
                        display: flex;
                        flex-direction: column;
                        padding: 10px;
                    }

                    .icon {
                        flex-shrink: 0;
                        width: 25px;
                        height: 25px;
                    }
                }
            }

            .titles {
                position: relative;
                display: flex;
                flex-direction: column;
                gap: 10px;

                li {
                    position: relative;
                    display: flex;
                    justify-content: flex-start;
                    align-items: center;
                    min-height: 62px;
                    background-color: $background-primary;
                    border-radius: 10px;
                    padding: 10px;
                    gap: 10px;

                    .left {
                        flex-shrink: 0;
                    }

                    @each $rarity in $rarities {
                        .#{$rarity} {
                            color: var(--token-arise-theme-1-rarity-rarity-#{$rarity}-light);
                        }
                    }

                    .lockedIcon {
                        width: 40px;
                        fill: $foreground-tertiary;
                    }

                    .hintButton {
                        padding: 2px 12px;
                        margin-left: auto;
                        background-color: $background-highlight;
                        color: $foreground-primary;
                        border-radius: 5px;
                    }
                }
            }
        }
    }
}
